import { type FC, useState } from 'react';
import { PiFunnelSimple } from 'react-icons/pi';
import Button from '../../../atoms/buttons/button';
import { FilterBuilder } from './filter-builder';
import {
  type FilterFieldOptions,
  type Filter,
  type StatusOption,
} from 'src/modules/shared/types/filters.types';
import { Text } from '../../../atoms/text';
import { CountBadge } from '../../../atoms/count-badge';
import { getTextSize } from 'src/modules/shared/components/atoms/text/text.styles';
import Modal from '../../modals/modal';
import { ModalFooter } from '../../modals/modal-footer';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

export interface AdvancedFiltersProps {
  fieldsOptions: FilterFieldOptions[];
  onChange: (filters: Filter[]) => void;
  activeFilterCount: number;
  filters: Filter[];
  userOptions?: { id: number | string; name: string }[];
  statusOptions?: StatusOption[];
  propertyOptions?: { id: number; label: string; value: string }[];
  propertyLoading?: boolean;
  onPropertyQueryChange?: (query: string) => void;
}

export const AdvancedFilters: FC<AdvancedFiltersProps> = ({
  fieldsOptions,
  onChange,
  activeFilterCount = 0,
  filters: initialFilters,
  userOptions,
  statusOptions = [],
  propertyOptions = [],
  propertyLoading = false,
  onPropertyQueryChange,
}) => {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<Filter[]>(initialFilters);

  const handleOpen = () => {
    setLocalFilters(initialFilters);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setLocalFilters(initialFilters);
  };

  const handleApply = () => {
    onChange(localFilters);
    setIsOpen(false);
  };

  const filterButtonClasses = `flex items-center gap-2 px-3 ${activeFilterCount > 0 ? 'bg-primary-50' : ''}`;
  const filterButton = (
    <Button
      appearance="ghost"
      size="md"
      className={filterButtonClasses}
      onClick={handleOpen}
    >
      <div className="flex items-center gap-2">
        <PiFunnelSimple className="w-5 h-5" />
        <Text size={getTextSize('md')} className="hidden 2xl:inline">
          {t('shared.advancedFilters.button')}
        </Text>
        {activeFilterCount > 0 && (
          <CountBadge count={activeFilterCount} size="sm" variant="primary" />
        )}
      </div>
    </Button>
  );

  return (
    <>
      {filterButton}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        header={
          <div className="space-y-1">
            <Text
              as="h3"
              size="h6"
              weight="semibold"
              className="text-primary-900"
            >
              {t('shared.advancedFilters.title')}
            </Text>
            <Text size="small" className="text-neutral-600">
              {t('shared.advancedFilters.description')}
            </Text>
          </div>
        }
        body={
          <div className="min-h-0 w-full overflow-y-auto bg-neutral-50 p-4 sm:p-5">
            <FilterBuilder
              fieldsOptions={fieldsOptions}
              filters={localFilters}
              onChange={setLocalFilters}
              userOptions={userOptions}
              statusOptions={statusOptions}
              propertyOptions={propertyOptions}
              propertyLoading={propertyLoading}
              onPropertyQueryChange={onPropertyQueryChange}
            />
          </div>
        }
        footer={
          <ModalFooter
            onCancel={handleClose}
            onConfirm={handleApply}
            cancelText={t('shared.actions.cancel')}
            confirmText={t('shared.actions.apply')}
          />
        }
      />
    </>
  );
};
